import React, { useState, useEffect } from 'react';
import StepBuilder from './step_builder';
import DragDropList from '../shared/drag_drop_list';

const WorkflowBuilder = ({ initialWorkflow, isEdit = false }) => {
  const defaultEmailTemplate = "You have been invited to complete the onboarding process for {{site_name}}.\n\nPlease click the link below to begin the onboarding process. You'll be guided through a series of steps to provide the necessary information and documents.";
  
  const [workflow, setWorkflow] = useState(
    initialWorkflow || {
      name: '',
      description: '',
      start_email_template: defaultEmailTemplate,
      active: true,
      workflow_steps: []
    }
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Effect to handle body scroll when modal is open
  useEffect(() => {
    if (showPreviewModal) {
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scrolling
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPreviewModal]);

  const handleWorkflowChange = (field, value) => {
    setWorkflow(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleStepChange = (stepIndex, updatedStep) => {
    setWorkflow(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps.map((step, index) => 
        index === stepIndex ? { ...updatedStep, position: index + 1 } : step
      )
    }));
  };

  const handleStepDelete = (stepIndex) => {
    setWorkflow(prev => ({
      ...prev,
      workflow_steps: prev.workflow_steps
        .filter((_, index) => index !== stepIndex)
        .map((step, index) => ({ ...step, position: index + 1 }))
    }));
  };

  const handleStepsReorder = (reorderedSteps) => {
    const stepsWithPositions = reorderedSteps.map((step, index) => ({
      ...step,
      position: index + 1
    }));
    
    setWorkflow(prev => ({
      ...prev,
      workflow_steps: stepsWithPositions
    }));
  };

  const addStep = (stepType = 'document_upload') => {
    const newStep = {
      id: `new_${Date.now()}`,
      title: '',
      description: '',
      instructions: '',
      step_type: stepType,
      requirement_type: 'required',
      signature_required: false,
      position: workflow.workflow_steps.length + 1,
      questionnaire_id: null
    };

    setWorkflow(prev => ({
      ...prev,
      workflow_steps: [...prev.workflow_steps, newStep]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!workflow.name?.trim()) {
      newErrors.name = 'Workflow name is required';
    }
    
    workflow.workflow_steps.forEach((step, index) => {
      const stepErrors = {};
      
      if (!step.title?.trim()) {
        stepErrors.title = 'Step title is required';
      }
      
      if (step.step_type === 'questionnaire' && !step.questionnaire_id) {
        stepErrors.questionnaire_id = 'Questionnaire selection is required';
      }
      
      if (Object.keys(stepErrors).length > 0) {
        newErrors[`step_${index}`] = stepErrors;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (saveType = 'save') => {
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.is-invalid, .text-danger');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const url = isEdit ? `/onboarding_workflows/${workflow.id}` : '/onboarding_workflows';
      const method = isEdit ? 'PATCH' : 'POST';

      const formData = new FormData();
      formData.append('onboarding_workflow[name]', workflow.name);
      formData.append('onboarding_workflow[description]', workflow.description || '');
      formData.append('onboarding_workflow[start_email_template]', workflow.start_email_template || '');
      formData.append('onboarding_workflow[active]', workflow.active);
      
      // Handle steps and template documents separately
      const stepsWithoutFiles = workflow.workflow_steps.map(step => {
        const stepCopy = { ...step };
        // Remove file object before JSON serialization
        delete stepCopy.template_document;
        return stepCopy;
      });
      
      formData.append('steps', JSON.stringify(stepsWithoutFiles));
      
      // Append template documents as separate FormData entries
      workflow.workflow_steps.forEach((step, index) => {
        if (step.template_document && step.template_document instanceof File) {
          formData.append(`step_${index}_template_document`, step.template_document);
        }
      });

      const response = await fetch(url, {
        method: method,
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (saveType === 'save_and_continue') {
            // Stay on current page
            swal('Success', 'Workflow was successfully saved.', 'success');
          } else {
            // Redirect to show page
            window.location.href = `/onboarding_workflows/${data.workflow.id}`;
          }
        } else {
          setErrors(data.errors || {});
        }
      } else {
        const errorData = await response.json();
        setErrors(errorData.errors || {});
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      swal('Error', 'Error saving workflow', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getAvailableQuestionnaires = async () => {
    try {
      const response = await fetch('/questionnaires.json');
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
      return [];
    }
  };

  const renderStep = (step, index) => (
    <StepBuilder
      key={step.id || `step_${index}`}
      step={step}
      index={index}
      onChange={(updatedStep) => handleStepChange(index, updatedStep)}
      onDelete={() => handleStepDelete(index)}
      getAvailableQuestionnaires={getAvailableQuestionnaires}
      errors={errors[`step_${index}`] || {}}
    />
  );

  const handlePreview = async () => {
    setPreviewLoading(true);
    setShowPreviewModal(true);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

      const response = await fetch('/onboarding_workflows/preview_email_draft', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_email_template: workflow.start_email_template
        })
      });

      const previewData = await response.json();
      
      if (previewData.success) {
        setPreviewContent(previewData.html);
      } else {
        setPreviewContent(`<div class="alert alert-danger">Error: ${previewData.error}</div>`);
      }
    } catch (error) {
      setPreviewContent(`<div class="alert alert-danger">Error loading preview: ${error.message}</div>`);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="workflow-builder">
      <div className="card">
        <div className="card-header bg-onboarding text-white d-flex">
          <h5 className="mb-0">
            <i className="fa fa-list-ol mr-2"></i>
            {isEdit ? 'Edit Workflow' : 'Create New Workflow'}
          </h5>
        </div>
        <div className="card-body">
          {Object.keys(errors).length > 0 && (
            <div className="alert alert-danger alert-dismissible mb-4" role="alert">
              <h6 className="alert-heading mb-2">
                <i className="fa fa-exclamation-triangle mr-2"></i>
                Please fix the following errors:
              </h6>
              <ul className="mb-0 pl-3">
                {Object.entries(errors).map(([key, error]) => {
                  if (key === 'name') {
                    return <li key={key}>Workflow name: {error}</li>;
                  } else if (key.startsWith('step_')) {
                    const stepIndex = parseInt(key.replace('step_', '')) + 1;
                    return Object.entries(error).map(([field, message]) => (
                      <li key={`${key}_${field}`}>
                        Step {stepIndex} ({field}): {message}
                      </li>
                    ));
                  }
                  return <li key={key}>{error}</li>;
                })}
              </ul>
            </div>
          )}
          <div className="row">
            <div className="col-md-8">
              <div className="form-group">
                <label className="form-label">Workflow Name</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={workflow.name}
                  onChange={(e) => handleWorkflowChange('name', e.target.value)}
                  placeholder="Enter workflow name..."
                />
                {errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  value={workflow.active}
                  onChange={(e) => handleWorkflowChange('active', e.target.value === 'true')}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows="3"
              value={workflow.description}
              onChange={(e) => handleWorkflowChange('description', e.target.value)}
              placeholder="Enter workflow description..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Start Email Template</label>
            <textarea
              className="form-control"
              rows="6"
              value={workflow.start_email_template}
              onChange={(e) => handleWorkflowChange('start_email_template', e.target.value)}
              placeholder={`Enter your custom message that will appear at the top of the email...

Available variables:
${'{{site_name}}'} - The name of the site

Your message will be followed by instructions and the onboarding link (added automatically).`}
            />
            <small className="form-text text-muted">
              <strong>Email Structure:</strong> Your custom message will appear at the top, followed by instructions and the onboarding link (both added automatically).
              <br />
              You can use the variable <code>{'{{site_name}}'}</code> which will be replaced with the actual site name.
              <br />
              <strong>Note:</strong> The default message is shown above. Modify it to customize your email. Use the preview button to see how your email will look.
            </small>
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handlePreview}
                disabled={previewLoading}
              >
                {previewLoading ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2"></i>
                    Loading Preview...
                  </>
                ) : (
                  <>
                    <i className="fa fa-eye mr-2"></i>
                    Preview Email
                  </>
                )}
              </button>
              <small className="form-text text-muted mt-1">
                Preview how your email will look with the current template.
              </small>
            </div>
          </div>

          <div className="workflow-steps-section">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Workflow Steps</h6>
            </div>

            {workflow.workflow_steps.length > 0 ? (
              <>
                <DragDropList
                  items={workflow.workflow_steps}
                  onReorder={handleStepsReorder}
                  renderItem={renderStep}
                  className="workflow-steps-list"
                />
                <div className="text-center mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => addStep('document_upload')}
                  >
                    <i className="fa fa-plus"></i> Add Step
                  </button>
                </div>
              </>
            ) : (
              <div className="empty-state text-center py-4">
                <h6 className="text-muted">No steps yet</h6>
                <p className="text-muted">Add your first step to get started.</p>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => addStep('document_upload')}
                >
                  <i className="fa fa-plus"></i> Add First Step
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="card-body">
          <div className="d-flex">
            <div className="ml-auto">
              <button
                type="button"
                className="btn btn-primary mr-2"
                onClick={() => handleSave('save')}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save"></i> Save
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline-default"
                onClick={() => window.location.href = '/onboarding_workflows'}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Preview Modal */}
      {showPreviewModal && (
        <>
          <div className="modal-backdrop fade show" style={{ position: 'fixed', zIndex: 1040 }}></div>
          <div 
            className="modal fade show" 
            style={{
              display: 'block', 
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1050,
              overflow: 'hidden'
            }} 
            tabIndex="-1" 
            role="dialog"
          >
            <div 
              className="modal-dialog modal-xl" 
              role="document"
              style={{
                height: '100%',
                maxHeight: '100vh',
                margin: '1.75rem auto',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <div 
                className="modal-content"
                style={{
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div className="modal-header" style={{ flexShrink: 0, paddingBottom: '15px' }}>
                  <h5 className="modal-title">
                    <i className="fa fa-eye mr-2"></i>
                    Email Preview
                  </h5>
                  <button 
                    type="button" 
                    className="close" 
                    onClick={() => setShowPreviewModal(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div 
                  className="modal-body"
                  style={{
                    flexGrow: 1,
                    overflow: 'auto',
                    maxHeight: 'calc(90vh - 120px)',
                    paddingTop: '15px'
                  }}
                >
                  {previewLoading ? (
                    <div className="text-center py-4">
                      <i className="fa fa-spinner fa-spin mr-2"></i>
                      Loading email preview...
                    </div>
                  ) : (
                    <div 
                      className="email-preview-content"
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa'
                      }}
                      dangerouslySetInnerHTML={{__html: previewContent}}
                    />
                  )}
                </div>
                <div className="modal-footer" style={{ flexShrink: 0, marginTop: '20px' }}>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowPreviewModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      <style>{`
        .empty-state {
          border: 2px dashed #dee2e6;
          border-radius: 0.25rem;
          margin: 2rem 0;
        }
      `}</style>
    </div>
  );
};

export default WorkflowBuilder;